import useUpdateStreams from '@hooks/useUpdateStreams';
import { IStoreState } from '@store/index';
import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Menu, MenuItem, SvgIcon, Typography } from '@mui/material';
import SearchHeader from '@components/common/SearchHeader';
import Utils from '@utils/index';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useMobileQuery } from '@hooks/useMobileQuery';
import { useSmallScreenQuery } from '@hooks/useSmallScreenQuery';
import CustomDataGrid from '@components/common/DataGrid/CustomDataGrid';
import { useNavigate } from 'react-router-dom';
import { last } from 'lodash';
import { useSnackbar } from 'notistack';
import NonIdealState from '@components/common/NonIdealState/NonIdealState';
import { StorageOutlined, VideocamOutlined } from '@mui/icons-material';
import ConfirmationDialog from '@components/common/ConfirmationDialog';
import API from '@API/index';
import { useDispatch } from 'react-redux';
import { setStreamList } from '@store/streams/actions';
import CustomStreamsConfiguration from '@components/views/CustomStreamsConfiguration';
import StreamsCVParametersEditor from '@components/views/StreamsCVParametersEditor';

const DEFAULT_VISIBILITY = {
  kvsName: true,
  companyId: false,
  siteId: true,
  iotThingName: true,
  rtspUrl: false,
  rtspUrlProc: false,
  defaultName: true,
  state: true,
  tags: false,
  type: false,
  manufacturer: true,
  position: false,
  deviceShadowName: true,
  retentionPeriodInHours: false,
  latitude: false,
  longitude: false,
  fovWidth: false,
  range: false,
  angleEastOfNorth: false,
  statusOnDevice: true,
  rportCameraId: false,
  createdAt: false,
  updateAt: false
};

const MOBILE_VISIBILITY = {
  kvsName: true,
  companyId: false,
  siteId: true,
  iotThingName: false,
  rtspUrl: false,
  rtspUrlProc: false,
  defaultName: false,
  state: false,
  tags: false,
  type: false,
  manufacturer: false,
  position: false,
  deviceShadowName: false,
  retentionPeriodInHours: false,
  latitude: false,
  longitude: false,
  fovWidth: false,
  range: false,
  angleEastOfNorth: false,
  statusOnDevice: false,
  rportCameraId: false,
  createdAt: false,
  updateAt: false
}

const HealthCameras: React.FC<{}> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const selectedCompany = useSelector((store: IStoreState) => store.selectedCompanies.selectedCompany);
  const userData = useSelector((store: IStoreState) => store.session.userData)
  const streams = useSelector((store: IStoreState) => store.streams.streams);
  const isSuperAdministrator = useSelector((store: IStoreState) => store.session.isSuperAdministrator);
  const selectedSite = useSelector((store: IStoreState) => store.selectedSites.selectedSite);

  const isLoadingStreams = useUpdateStreams(selectedSite?.siteId, selectedCompany?.companyId, userData?.roles, userData?.userId);
  const isMobile = useMobileQuery();
  const isSmallScreen = useSmallScreenQuery();

  const [filteredStreams, setFilteredStreams] = React.useState<VideoStream[]>([]);
  const [finalStreams, setFinalStreams] = React.useState<VideoStream[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>(DEFAULT_VISIBILITY)
  const [selectedStreams, setSelectedStreams] = React.useState<string[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<any | null>(null);
  const [streamsToArchive, setStreamsToArchive] = React.useState<VideoStream[]>([]);
  const [streamsToDelete, setStreamsToDelete] = React.useState<VideoStream[]>([]);
  const [streamsToActivate, setStreamsToActivate] = React.useState<VideoStream[]>([]);
  const [streamsToConfigure, setStreamsToConfigure] = React.useState<VideoStream[]>([]);
  const [streamsToEditCV, setStreamsToEditCV] = React.useState<VideoStream[]>([]);
  const [performingAction, setPerformingAction] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (selectedSite) {
      setFilteredStreams(streams.filter(el => el.siteId === selectedSite.siteId));
    } else if (selectedCompany) {
      setFilteredStreams(streams.filter(el => el.companyId === selectedCompany?.companyId));
    } else {
      setFilteredStreams(streams)
    }
  }, [streams, selectedCompany, selectedSite])

  React.useEffect(() => {
    if (isMobile || isSmallScreen) {
      setColumnVisibilityModel(MOBILE_VISIBILITY)
    } else {
      setColumnVisibilityModel(DEFAULT_VISIBILITY)
    }
  }, [isMobile, isSmallScreen])

  const generateColumn = React.useCallback((property: string, label: string, flex: number = 0.25) => {
    return {
      field: property,
      headerName: label,
      flex: flex,
      sortable: true,
      renderCell: (params: any) => (
        <Typography noWrap variant="body2">{params.value}</Typography>
      )
    }
  }, []);

  const generateCustomColumn = React.useCallback((property: string, label: string, flex: number = 0.25) => {
    return {
      field: property,
      headerName: label,
      flex: flex,
      sortable: true,
      renderCell: (params: any) => {
        if (params.value === "CAMERA") {
          return (
            <SvgIcon fontSize='small' >
              <VideocamOutlined fontSize='small' />
            </SvgIcon>
          )
        }

        return (
          <SvgIcon fontSize='small' >
            <StorageOutlined fontSize='small' />
          </SvgIcon>
        )
      }
    }
  }, []);

  const changeStreamStatus = React.useCallback(async (siteId: string, kvsName: string, status: "ACTIVE" | "ARCHIVED") => {
    try {
      await API.Streams.archiveStream(siteId, kvsName, status);
      return { kvsName, status: true }
    } catch (error) {
      return { kvsName, status: false, error, }
    }
  }, [])

  const deleteStream = React.useCallback(async (siteId: string, kvsName: string) => {
    try {
      await API.Streams.deleteStream(siteId, kvsName);
      return { kvsName, status: true }
    } catch (error) {
      return { kvsName, status: false, error, }
    }
  }, [])

  const handleActivateStreams = React.useCallback(async () => {
    try {
      setPerformingAction(true);
      const promises = await Promise.allSettled(streamsToActivate.map((stream: VideoStream) => changeStreamStatus(stream.siteId, stream.kvsName, "ACTIVE"))) as any[]
      const newStreams = [...streams];
      newStreams.forEach((e) => {
        const target = streamsToActivate.find(el => el.kvsName === e.kvsName);
        if (target && promises.find(el => el.value.kvsName === e.kvsName)?.status === "fulfilled") {
          e.state = "ACTIVE"
        }
      })

      dispatch(setStreamList(newStreams))
      setStreamsToActivate([]);
      setPerformingAction(false);
      enqueueSnackbar(`${promises.filter(el => el.status === "fulfilled").length} streams activated!`, { variant: 'success' })
      if (promises.filter(el => el.status === "rejected").length > 1) {
        enqueueSnackbar(`${promises.filter(el => el.status === "rejected").length} streams failed to be activated!`, { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(Utils.Error.getErrorMessage(error), { variant: 'error' })
      console.log(error);
    } finally {
      setPerformingAction(false);
    }
  }, [changeStreamStatus, dispatch, enqueueSnackbar, streams, streamsToActivate])

  const handleArchiveStreams = React.useCallback(async () => {
    try {
      setPerformingAction(true);
      const promises = await Promise.allSettled(streamsToArchive.map((stream: VideoStream) => changeStreamStatus(stream.siteId, stream.kvsName, "ARCHIVED"))) as any[]
      const newStreams = [...streams];
      newStreams.forEach((e) => {
        const target = streamsToArchive.find(el => el.kvsName === e.kvsName);
        if (target && promises.find(el => el.value.kvsName === e.kvsName)?.status === "fulfilled") {
          e.state = "ARCHIVED"
        }
      })

      dispatch(setStreamList(newStreams))
      setStreamsToArchive([]);
      setPerformingAction(false);
      enqueueSnackbar(`${promises.filter(el => el.status === "fulfilled").length} streams archived!`, { variant: 'success' })
      if (promises.filter(el => el.status === "rejected").length > 1) {
        enqueueSnackbar(`${promises.filter(el => el.status === "rejected").length} streams failed to be archived!`, { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(Utils.Error.getErrorMessage(error), { variant: 'error' })
      console.log(error);
    } finally {
      setPerformingAction(false);
    }
  }, [changeStreamStatus, dispatch, enqueueSnackbar, streams, streamsToArchive])

  const handleDeleteStreams = React.useCallback(async () => {
    try {
      setPerformingAction(true);
      const promises = await Promise.allSettled(streamsToDelete.map((stream: VideoStream) => deleteStream(stream.siteId, stream.kvsName))) as any[]
      const newStreams = [...streams].filter((stream: VideoStream) => {
        const target = promises.find(el => el.value.kvsName === stream.kvsName)
        if (target) {
          if (target.status === "fulfilled") {
            return false;
          }
        }
        return true;
      });

      dispatch(setStreamList(newStreams))
      setStreamsToDelete([]);
      setPerformingAction(false);
      enqueueSnackbar(`${promises.filter(el => el.status === "fulfilled").length} streams deleted!`, { variant: 'success' })
      if (promises.filter(el => el.status === "rejected").length > 1) {
        enqueueSnackbar(`${promises.filter(el => el.status === "rejected").length} streams failed to be deleted!`, { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(Utils.Error.getErrorMessage(error), { variant: 'error' })
      console.log(error);
    } finally {
      setPerformingAction(false);
    }
  }, [deleteStream, dispatch, enqueueSnackbar, streams, streamsToDelete])

  const textFilterProps = React.useMemo(() => ["siteId", "defaultName", "kvsName", "iotThingName", "state", "statusOnDevice", "manufacturer", "deviceShadowName"], []);

  const streamsToDisplay = React.useMemo(() => {
    if (isLoadingStreams) return [];

    return Utils.Array.orderAlphabetically(finalStreams, 'companyId').map((stream: VideoStream) => ({
      id: stream.kvsName,
      kvsName: stream.kvsName,
      companyId: stream.companyId,
      siteId: stream.siteId,
      iotThingName: stream.iotThingName,
      rtspUrl: stream.rtspUrl,
      rtspUrlProc: stream.rtspUrlProc,
      defaultName: stream.defaultName,
      state: stream.state,
      tags: stream.tags,
      type: stream.type,
      manufacturer: stream.manufacturer,
      position: stream.position,
      deviceShadowName: stream.deviceShadowName,
      retentionPeriodInHours: stream.retentionPeriodInHours,
      latitude: stream.latitude,
      longitude: stream.longitude,
      fovWidth: stream.fovWidth,
      range: stream.range,
      angleEastOfNorth: stream.angleEastOfNorth,
      statusOnDevice: stream.statusOnDevice,
      rportCameraId: stream.rportCameraId,
      createdAt: stream.createdAt,
      updateAt: stream.updatedAt,
      sourceType: stream.sourceType
    }))
  }, [finalStreams, isLoadingStreams])

  if (isSuperAdministrator === false) {
    return (
      <NonIdealState
        title="Not found"
        description="Page was not found, please check your information."
        errorCode={404}
      />
    )
  }

  return (
    <Box display="grid" gridTemplateRows="min-content auto" width="100%" height="100%" padding="1.5rem" gap="2rem">
      <SearchHeader
        title="Cameras"
        textFiltering={true}
        textFilterProps={textFilterProps}
        noPadding
        searchHeaderFilterProps={{
          onFilteredDataChange: setFinalStreams,
          data: filteredStreams,
          properties: Utils.Filters.createVideoStreamFilters(true)
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={selectedStreams.length === 0}
          onClick={(e) => setAnchorEl(e.target)}
        >
          Operation
        </Button>
      </SearchHeader>
      <CustomDataGrid
        loading={isLoadingStreams}
        hideHeaders={isLoadingStreams || filteredStreams.length === 0}
        defaultHeight='82vh'
        disableColumnFilter
        rows={streamsToDisplay}
        rowSelectionModel={selectedStreams}
        onRowSelectionModelChange={(newSelectionModel) => {
          setSelectedStreams(newSelectionModel as string[]);
        }}
        checkboxSelection
        columns={[
          generateCustomColumn('sourceType', 'Source', 0.5),
          generateColumn('companyId', 'Company ID', 1),
          generateColumn('siteId', 'Site ID', 1),
          generateColumn('defaultName', 'Name', 1),
          generateColumn('kvsName', 'KVS name', 1.5),
          generateColumn('iotThingName', 'IOT thing name', 1),
          generateColumn('rtspUrl', 'RTSP url', 1),
          generateColumn('rtspUrlProc', 'RTSP url proc', 1),
          generateColumn('rportCameraId', 'RPORT ID', 1),
          generateColumn('state', 'State', 0.5),
          generateColumn('statusOnDevice', 'Status', 0.5),
          generateColumn('tags', 'Tags', 0.5),
          generateColumn('type', 'Type', 0.5),
          generateColumn('manufacturer', 'Manufacturer', 0.5),
          generateColumn('cameraModel', 'Model', 0.5),
          generateColumn('position', 'Position', 0.5),
          generateColumn('deviceShadowName', 'Device shadow name', 0.8),
          generateColumn('latitude', 'Latitude', 0.5),
          generateColumn('longitude', 'Longitude', 0.5),
          generateColumn('fovWidth', 'FOV width', 0.5),
          generateColumn('range', 'Range', 0.5),
          generateColumn('angleEastOfNorth', 'Angle east of north', 0.5),
          generateColumn('createdAt', 'Created', 0.5),
          generateColumn('updateAt', 'Updated', 0.5),
        ]}
        autoPageSize
        onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
        columnVisibilityModel={columnVisibilityModel}
        dataCy="overview-cameras-table"
        onRowClick={(params) => {
          try {
            const url = Utils.Stream.getStreamShareUrl({ kvsName: params.id } as VideoStream);
            console.log(`/${last(url.split('/'))}`)
            navigate(`/${last(url.split('/'))}`);
          } catch (error) {
            enqueueSnackbar('There was a problem navigating to the stream', { variant: 'error' })
          }
        }}
      />
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setStreamsToActivate(() => streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ARCHIVED"))
            setAnchorEl(null)
          }}
          disabled={streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ARCHIVED").length === 0}
        >
          Activate
        </MenuItem>
        <MenuItem
          onClick={() => {
            setStreamsToArchive(() => streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ACTIVE"))
            setAnchorEl(null)
          }}
          disabled={streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ACTIVE").length === 0}
        >
          Archive
        </MenuItem>
        <MenuItem
          onClick={() => {
            setStreamsToDelete(() => streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ARCHIVED"))
            setAnchorEl(null)
          }}
          disabled={streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ARCHIVED").length === 0}
        >
          Delete
        </MenuItem>
        <MenuItem
          onClick={() => {
            setStreamsToConfigure(() => streams.filter(el => selectedStreams.includes(el.kvsName)))
            setAnchorEl(null)
          }}
        >
          Advanced feature configuration
        </MenuItem>
        <MenuItem
          onClick={() => {
            setStreamsToEditCV(() => streams.filter(el => selectedStreams.includes(el.kvsName)))
            setAnchorEl(null)
          }}
        >
          Edit CV Parameters
        </MenuItem>
      </Menu>
      <ConfirmationDialog
        title={"Confirm action"}
        description={(
          <>
            <Typography>
              <span>Are you sure you want to </span><b>ACTIVATE</b><span>{` the stream${streamsToActivate.length > 1 ? "s" : ""}?`}</span>
            </Typography>
            {streamsToActivate.map((el) => (
              <Typography style={{ overflow: "hidden", whiteSpace: "nowrap", maxWidth: "20rem", textOverflow: "ellipsis" }}>
                <strong>{el.defaultName || el.kvsName}</strong>
              </Typography>
            ))}
            <Box height="1rem" ></Box>
            {streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ACTIVE").length > 0 &&
              <>
                <Typography>
                  <span>Already active streams:</span>
                </Typography>
                {streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ACTIVE").map((el) => (
                  <Typography style={{ overflow: "hidden", whiteSpace: "nowrap", maxWidth: "20rem", textOverflow: "ellipsis" }}>
                    <strong>{el.defaultName || el.kvsName}</strong>
                  </Typography>
                ))}
              </>
            }
          </>
        )}
        onCancel={() => setStreamsToActivate([])}
        transitionDuration={0}
        onConfirm={handleActivateStreams}
        open={streamsToActivate.length > 0}
        loading={performingAction}
      />
      <ConfirmationDialog
        title={"Confirm action"}
        description={(
          <>
            <Typography>
              <span>Are you sure you want to </span><b>ARCHIVE</b><span>{` the stream${streamsToArchive.length > 1 ? "s" : ""}?`}</span>
            </Typography>
            {streamsToArchive.map((el) => (
              <Typography style={{ overflow: "hidden", whiteSpace: "nowrap", maxWidth: "20rem", textOverflow: "ellipsis" }}>
                <strong>{el.defaultName || el.kvsName}</strong>
              </Typography>
            ))}
            <Box height="1rem" ></Box>
            {streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ARCHIVED").length > 0 &&
              <>
                <Typography>
                  <span>Already archived streams:</span>
                </Typography>
                {streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ARCHIVED").map((el) => (
                  <Typography style={{ overflow: "hidden", whiteSpace: "nowrap", maxWidth: "20rem", textOverflow: "ellipsis" }}>
                    <strong>{el.defaultName || el.kvsName}</strong>
                  </Typography>
                ))}
              </>
            }
          </>
        )}
        onCancel={() => setStreamsToArchive([])}
        transitionDuration={0}
        onConfirm={handleArchiveStreams}
        open={streamsToArchive.length > 0}
        loading={performingAction}
      />
      <ConfirmationDialog
        title={"Confirm action"}
        description={(
          <>
            <Typography>
              <span>Are you sure you want to </span><b>DELETE</b><span>{` the stream${streamsToDelete.length > 1 ? "s" : ""}?`}</span>
            </Typography>
            {streamsToDelete.map((el) => (
              <Typography style={{ overflow: "hidden", whiteSpace: "nowrap", maxWidth: "20rem", textOverflow: "ellipsis" }}>
                <strong>{el.defaultName || el.kvsName}</strong>
              </Typography>
            ))}
            <Box height="1rem" ></Box>
            {streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ARCHIVED").length > 0 &&
              <>
                <Typography>
                  <span>Active streams that cannot be deleted:</span>
                </Typography>
                {streams.filter(el => selectedStreams.includes(el.kvsName) && el.state === "ACTIVE").map((el) => (
                  <Typography style={{ overflow: "hidden", whiteSpace: "nowrap", maxWidth: "20rem", textOverflow: "ellipsis" }}>
                    <strong>{el.defaultName || el.kvsName}</strong>
                  </Typography>
                ))}
              </>
            }
          </>
        )}
        onCancel={() => setStreamsToDelete([])}
        transitionDuration={0}
        onConfirm={handleDeleteStreams}
        open={streamsToDelete.length > 0}
        loading={performingAction}
      />
      {streamsToConfigure.length > 0 &&
        <CustomStreamsConfiguration
          streams={streamsToConfigure}
          staticMode
          onClose={() => setStreamsToConfigure([])}
        />
      }
      {streamsToEditCV.length > 0 &&
        <StreamsCVParametersEditor
          streams={streamsToEditCV}
          onClose={() => setStreamsToEditCV([])}
        />
      }
    </Box>
  )
}

export default HealthCameras;
